import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { MyAffiliateLedger } from "@/features/affiliates/components/MyAffiliateLedger";
import { MyAffiliateLink } from "@/features/affiliates/components/MyAffiliateLink";
import { MyAffiliateProductPicker } from "@/features/affiliates/components/MyAffiliateProductPicker";
import { getMyAffiliateCode, type MyAffiliateCode } from "@/features/affiliates/my-code-api";
import { pickReferralLink, type ReferralLink } from "@/features/affiliates/referral-link";
import { StaffName } from "@/components/StaffName";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function MyAffiliateCodeDialog({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Ruột chỉ DỰNG khi hộp mở, nên mỗi lần mở là một lượt tải mới mà không
          cần đặt lại trạng thái bằng tay trong effect. "Đang tải" là giá trị
          KHỞI TẠO, không phải thứ effect bật lên — đặt trạng thái ngay trong
          effect đẻ ra một lượt vẽ thừa và eslint chặn. Hành vi người dùng thấy
          y như trước: mở hộp là hiện vòng xoay rồi ra mã. */}
      {open ? <NoiDung onClose={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function NoiDung({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState<MyAffiliateCode | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMyAffiliateCode()
      .then((data) => {
        if (!cancelled) setCode(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCode(null);
          setError(err instanceof Error ? err.message : "Không đọc được mã AFF");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copyCode = async () => {
    if (!code?.code) return;
    await navigator.clipboard.writeText(code.code);
    toast.success("Đã copy mã AFF");
  };

  const active = code ? code.enabled && code.status === "hoat_dong" : false;
  // Link chỉ có khi AFF đã phát (đi kèm sổ) và mã đang hoạt động — mã bị tắt thì
  // link không còn ghi công, không bày ra cho nhân viên gửi khách.
  const link = active ? pickReferralLink(code?.ledger?.links) : undefined;
  // Ô «Chọn sản phẩm» (bản 2, CEO 1a · 2a · 3a 30/10/2026): chỉ khi có sổ và AFF
  // đã phát link — sổ chưa có link nào nghĩa là AFF chưa có đích để dẫn về, xin
  // link món lúc đó cũng không về.
  const ledgerLinks: ReferralLink[] = active
    ? (code?.ledger?.links ?? []).filter((l) => l.url.startsWith("https://"))
    : [];
  // Đọc lại sổ khi đang chờ link món mới về; lỗi thì để vòng chờ thử lần sau.
  const reload = useCallback(async () => {
    setCode(await getMyAffiliateCode());
  }, []);

  const copyLink = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    toast.success("Đã copy link giới thiệu");
  };

  return (
    // Hộp TỰ quản cuộn: đầu và chân đứng yên, chỉ thân cuộn — nút "Copy mã AFF"
    // luôn nằm trong tầm tay dù sổ dài (mẫu CEO duyệt 1a, 01/10/2026). Lớp gốc
    // cho cả hộp cuộn, nên phải đè `overflow-hidden` ở CẢ hai khổ; thiếu
    // `md:overflow-hidden` thì ở máy tính chân hộp trôi theo nội dung.
    <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 md:overflow-hidden md:p-0">
      <div className="border-b border-border px-4 pb-3 pt-4 md:px-6 md:pt-6">
        <DialogHeader>
          <DialogTitle>Mã AFF của tôi</DialogTitle>
          <DialogDescription>
            Mã dùng khi khách giới thiệu đơn cho Gạo Nâu
            {code?.ledger ? ", và hoa hồng của bạn." : "."}
          </DialogDescription>
        </DialogHeader>
      </div>

      <div className="min-h-0 flex-1 space-y-3.5 overflow-y-auto px-4 py-4 md:px-6">
        {loading ? (
          <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Đang tải
          </div>
        ) : error ? (
          <div className="rounded-md border border-border bg-surface-2 p-3 text-sm text-muted-foreground">
            {error}
          </div>
        ) : code ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm text-muted-foreground">Chủ mã</div>
                <div className="font-medium">{code.ownerName ? <StaffName name={code.ownerName} /> : "—"}</div>
              </div>
              {active ? <Badge variant="green">Mã đang hoạt động</Badge> : null}
            </div>
            <div className="rounded-md border border-border bg-surface-2 p-4">
              <div className="text-sm text-muted-foreground">Mã AFF</div>
              <div className="break-all font-mono text-2xl font-semibold tracking-normal">
                {code.code}
              </div>
            </div>
            {link ? <MyAffiliateLink link={link} /> : null}
            {ledgerLinks.length > 0 ? (
              <MyAffiliateProductPicker links={ledgerLinks} reload={reload} />
            ) : null}
            {ledgerLinks.length === 0 && active && code.ledger ? (
              // Có sổ mà AFF chưa phát link (CEO chốt 3a, 01/10/2026): nói rõ là
              // sắp có, để nhân viên không tưởng mình bị thiếu. Đường gọi thẳng
              // (chưa có sổ) thì không nói gì — hộp như trước.
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>Link giới thiệu sẽ có khi mở đặt lịch qua link.</span>
              </p>
            ) : null}
            {!active ? (
              <div className="text-sm text-destructive">Mã đang chưa hoạt động.</div>
            ) : null}
            {code.ledger ? (
              <MyAffiliateLedger ledger={code.ledger} />
            ) : code.ledgerPending ? (
              <div className="rounded-md px-3 py-2.5 text-sm [background:var(--color-info-bg)] [color:var(--color-info-text)]">
                Sổ hoa hồng của bạn đang được đồng bộ. Mở lại sau ít phút để xem số đơn và tiền
                hoa hồng.
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <DialogFooter className="gap-2 border-t border-border px-4 py-3 sm:gap-0 md:px-6">
        <Button variant="secondary" onClick={onClose}>
          Đóng
        </Button>
        {link ? (
          <Button variant="outline" onClick={() => void copyLink()}>
            <Link2 className="h-4 w-4" />
            Copy link
          </Button>
        ) : null}
        <Button onClick={() => void copyCode()} disabled={!code?.code || loading}>
          <Copy className="h-4 w-4" />
          Copy mã AFF
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
